import { useState, useEffect, useRef } from 'react';
import { MessageCircle, Moon, Sun, X, Menu } from 'lucide-react';
import { BUSINESS_INFO } from '../data';
import { useTheme } from '../context/ThemeContext';
import { useLang } from '../context/LangContext';

export default function Navbar() {
  const [open, setOpen]       = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { dark, toggle: toggleTheme } = useTheme();
  const { lang, toggle: toggleLang, t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const close = () => setOpen(false);
  const scrollTo = (id, e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    close();
  };

  const NAV_LINKS = [
    ['collections', t.navCollections],
    ['gallery',     t.navLookbook],
    ['heritage',    t.navHeritage],
    ['visit',       t.navVisit],
  ];

  return (
    <>
      <nav
        id="navbar"
        style={{
          position:         'fixed',
          top:              0,
          left:             0,
          right:            0,
          zIndex:           100,
          height:           'var(--header-height)',
          display:          'flex',
          alignItems:       'center',
          justifyContent:   'space-between',
          padding:          '0 clamp(1.25rem, 5vw, 4rem)',
          transition:       'all 0.4s cubic-bezier(0.25,1,0.5,1)',
          backgroundColor:  scrolled ? 'var(--nav-bg)' : 'transparent',
          backdropFilter:   scrolled ? 'blur(24px) saturate(2)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(24px) saturate(2)' : 'none',
          borderBottom:     scrolled ? '1px solid var(--border)' : '1px solid transparent',
          boxShadow:        scrolled ? '0 4px 30px rgba(0,0,0,0.06)' : 'none',
        }}
      >
        {/* ── Logo ── */}
        <a
          href="#top"
          onClick={e => scrollTo('top', e)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', zIndex: 2 }}
        >
          <span
            style={{
              fontFamily:    'var(--font-serif)',
              fontSize:      '1.35rem',
              fontWeight:    500,
              letterSpacing: '0.08em',
              color:         'var(--text)',
              transition:    'color 0.3s',
            }}
          >
            ISHA <span style={{ color: 'var(--accent)' }}>BOUTIQUE</span>
          </span>
        </a>

        {/* ── Desktop Links ── */}
        <div className="desktop-nav" style={{ gap: '2.5rem' }}>
          {NAV_LINKS.map(([id, label]) => (
            <a key={id} href={`#${id}`} className="nav-link" onClick={e => scrollTo(id, e)}>
              {label}
            </a>
          ))}

          {/* Language toggle */}
          <button
            onClick={toggleLang}
            aria-label="Toggle language"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted)', fontWeight: 700, fontSize: '0.7rem',
              letterSpacing: '0.1em', transition: 'color 0.3s',
            }}
            onMouseOver={e => e.target.style.color = 'var(--accent)'}
            onMouseOut={e  => e.target.style.color = 'var(--text-muted)'}
          >
            {lang === 'en' ? 'IT' : 'EN'}
          </button>

          {/* Dark / Light toggle */}
          <button
            onClick={toggleTheme}
            aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text)', display: 'flex', alignItems: 'center',
              transition: 'color 0.3s, transform 0.3s',
            }}
            onMouseOver={e => e.currentTarget.style.transform = 'rotate(20deg)'}
            onMouseOut={e  => e.currentTarget.style.transform = 'rotate(0deg)'}
          >
            {dark ? <Sun size={17} /> : <Moon size={17} />}
          </button>

          <a
            href={BUSINESS_INFO.messenger}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{ padding: '0.6rem 1.5rem', fontSize: '0.72rem' }}
          >
            <MessageCircle size={14} />
            {t.messageUs}
          </a>
        </div>

        {/* ── Hamburger ── */}
        <button
          className="hamburger-btn"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--text)', zIndex: 2,
          }}
          onClick={() => setOpen(o => !o)}
          aria-label={open ? 'Close menu' : 'Open menu'}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* ── Mobile backdrop ── */}
      {open && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 98,
            background: 'rgba(10,8,10,0.6)',
            backdropFilter: 'blur(6px)',
          }}
          onClick={close}
        />
      )}

      {/* ── Mobile Menu ── */}
      <div
        className="glass"
        style={{
          position:       'fixed',
          top:            'var(--header-height)',
          left:           0,
          right:          0,
          zIndex:         99,
          maxHeight:      open ? '100vh' : '0',
          height:         open ? '100vh' : '0',
          overflow:       open ? 'auto' : 'hidden',
          overflow:       'hidden',
          opacity:        open ? 1 : 0,
          transition:     'all 0.4s cubic-bezier(0.25,1,0.5,1)',
          padding:        open ? '2rem clamp(1.25rem,5vw,4rem)' : '0 clamp(1.25rem,5vw,4rem)',
          display:        'flex',
          flexDirection:  'column',
          gap:            '0',
          borderBottom:   open ? '1px solid var(--border)' : 'none',
        }}
      >
        {NAV_LINKS.map(([id, label]) => (
          <a
            key={id}
            href={`#${id}`}
            onClick={e => scrollTo(id, e)}
            style={{
              display: 'block', padding: '1.5rem 0',
              borderBottom: '1px solid var(--border)',
              fontFamily: 'var(--font-sans)', fontWeight: 600,
              fontSize: '0.85rem', letterSpacing: '0.1em', textTransform: 'uppercase',
              color: 'var(--text)', transition: 'color 0.3s',
            }}
            onMouseOver={e => e.target.style.color = 'var(--accent)'}
            onMouseOut={e  => e.target.style.color = 'var(--text)'}
          >
            {label}
          </a>
        ))}

        <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1.5rem', alignItems: 'center' }}>
          <button
            onClick={() => { toggleLang(); close(); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted)', fontWeight: 700, fontSize: '0.75rem',
              letterSpacing: '0.1em',
            }}
          >
            {lang === 'en' ? 'SWITCH TO ITALIAN' : 'SWITCH TO ENGLISH'}
          </button>

          <button
            onClick={() => { toggleTheme(); }}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '0.6rem',
              fontSize: '0.85rem', fontWeight: 600, padding: '1rem 0.5rem',
              minHeight: '54px', zIndex: 10
            }}
          >
            {dark ? <Sun size={15} /> : <Moon size={15} />}
            {dark ? 'Light Mode' : 'Dark Mode'}
          </button>
        </div>

        <a
          href={BUSINESS_INFO.messenger}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary"
          onClick={close}
          style={{ marginTop: '1.5rem', padding: '0.875rem', justifyContent: 'center' }}
        >
          <MessageCircle size={16} /> {t.messageUs}
        </a>
      </div>
    </>
  );
}
